import { Typography, Grid, Box } from '@mui/material'
import React, { useEffect, useState } from 'react'
import SurveysList from '../components/surveys/SurveysList'
import FilterHeader from '../components/surveys/FilterHeader'
import TakeOffSheet from '../components/surveys/TakeOffSheet'
import { useTokenClaims } from '../security/useTokenClaims'
import { Survey, SurveyFilters } from '../types/Work/Survey'
import { RequestWorker } from '../types/Work/Request'
import { getQuantitySurveys, getSurveyAssignees } from '../services/quantitySurveyService'
import { ManageLTWorkersDialog } from '../components/surveys/ManageLTWorkersDialog'

export default function QuantitySurveys() {
	const [isListExpanded, setIsListExpanded] = useState(true)
	const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null)
	const { role, id } = useTokenClaims()
	const [assignees, setAssignees] = useState<RequestWorker[]>([])
	const [surveys, setSurveys] = useState<Survey[]>([])
	const [surveysLoading, setSurveysLoading] = useState(false)
	const [allWorkItemsCount, setAllWorkItemsCount] = useState(0)
	// Initialize filters based on user role
	const [filters, setFilters] = useState<SurveyFilters>({
		surveyId: null,
		status: null,
		assignedToId: null,
		assignedOn: null,
		searchWorkItem: null,
		page: 0,
		requestedOn: null,
		viewMode: role.title === 'LineTech' ? 'pending' : null,
		surveyedOn: null,
	})

	const fetchSurveys = async (showLoading = true) => {
		if (showLoading) {
			setSurveysLoading(true)
		}
		try {
			const filtersCopy = { ...filters }
			if (role.title === 'LineTech') {
				filtersCopy.assignedToId = id
			}
			const surveyResponse = await getQuantitySurveys(filtersCopy)
			setSurveys(surveyResponse.surveys)
			setAllWorkItemsCount(surveyResponse.allSurveysCount)
		} finally {
			setSurveysLoading(false)
		}
	}

	// Effect to fetch assignees on mount
	useEffect(() => {
		const fetchAssignees = async () => {
			if (role.title !== 'LineTech') {
				const assigneesResponse = await getSurveyAssignees()
				setAssignees(assigneesResponse)
			}
		}

		fetchAssignees()
	}, [role.title])

	useEffect(() => {
		fetchSurveys()
	}, [filters, role.title, id])

	const handleSurveyClick = (surveyId: number) => {
		setSelectedSurveyId(surveyId)
	}

	return (
		<div className='page-layout'>
			<Box display={'flex'} justifyContent={'space-between'}>
				<Typography
					sx={{
						fontSize: '1.5rem',
						paddingLeft: '1rem',
						paddingTop: '.5rem',
						zIndex: '3',
					}}
				>
					Surveys
				</Typography>

				<ManageLTWorkersDialog />
			</Box>

			{role.title !== 'LineTech' && (
				<FilterHeader filters={filters} setFilters={setFilters} assignees={assignees} />
			)}
			<Grid container spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
				<Grid
					item
					xs={12}
					sm={12}
					md={isListExpanded ? 5 : 1}
					lg={isListExpanded ? 5 : 0.8}
					xl={isListExpanded ? 4 : 0.6}
					sx={{
						transition: 'all 0.3s ease',
					}}
				>
					<SurveysList
						isExpanded={isListExpanded}
						onToggleExpand={() => setIsListExpanded(!isListExpanded)}
						selectedSurveyId={selectedSurveyId}
						onSurveyClick={handleSurveyClick}
						filters={filters}
						setFilters={setFilters}
						assignees={assignees}
						loadData={fetchSurveys}
						surveys={surveys}
						surveysLoading={surveysLoading}
						allWorkItemsCount={allWorkItemsCount}
					/>
				</Grid>
				<Grid
					item
					xs={12}
					sm={12}
					md={isListExpanded ? 7 : 11}
					lg={isListExpanded ? 7 : 11.2}
					xl={isListExpanded ? 8 : 11.4}
					sx={{
						transition: 'all 0.3s ease',
					}}
				>
					<TakeOffSheet
						selectedSurveyId={selectedSurveyId}
						loadData={fetchSurveys}
						setSearchWorkItem={(workItemName: string) => {
							setFilters((prev) => ({ ...prev, searchWorkItem: workItemName, page: 0 }))
						}}
					/>
				</Grid>
			</Grid>
		</div>
	)
}
